<script lang="ts">
  import type { Dataset } from '../types.ts';
  import type { ConditionTrack } from '../tracking/collate.ts';
  import { fmtDate, fmtMonth, ms } from '../format.ts';
  import CourseTimeline from '../charts/CourseTimeline.svelte';

  let { ds, tracks }: { ds: Dataset; tracks: ConditionTrack[] } = $props();
  const domain = $derived<[number, number]>([ms(ds.visits[0].date) - 20 * 86400000, ms(ds.visits[ds.visits.length - 1].date) + 60 * 86400000]);
  const eras = $derived(ds.summary.eras.map((e) => ({ start: ms(e.start), end: e.end ? ms(e.end) : null, label: e.label })));
  const events = $derived([...ds.medicationEvents].sort((a, b) => ms(b.date) - ms(a.date)));
  const treats = (drug: string) => tracks.filter((t) => t.spec.medications.some((p) => drug.toLowerCase().startsWith(p.toLowerCase()))).map((t) => t.spec.shortName);
  const verb: Record<string, string> = { start: 'Started', increase: 'Increased', decrease: 'Reduced', stop: 'Stopped' };
</script>

<div class="meds">
  <section class="card">
    <h2>Courses <span class="t-foot">{ds.medicationCourses.length} drugs over {Math.round((domain[1] - domain[0]) / (365.25 * 86400000))} years · hover a bar for the dose</span></h2>
    <CourseTimeline courses={ds.medicationCourses} {domain} {eras} />
  </section>

  <section>
    <h2>Current medications</h2>
    <div class="curr">
      {#each ds.summary.currentMedications as m (m.name)}
        <div class="mcard">
          <b>{m.name}</b><span>{m.dose}</span>
          <span class="t-foot">{m.class} · {m.route} · since {fmtMonth(m.since)}</span>
          <span class="for">{#each treats(m.name) as c (c)}<span class="chip">{c}</span>{/each}</span>
        </div>
      {/each}
    </div>
  </section>

  <section class="two">
    <div>
      <h2>Adjustments <span class="t-foot">{events.length}, newest first</span></h2>
      <ol class="evs">
        {#each events as e (e.date + e.drug + e.action)}
          <li class={e.action}>
            <span class="d">{fmtDate(e.date)}</span>
            <div><span class="t"><b>{verb[e.action]}</b> {e.drug}{#if e.dose} — {e.dose}{/if}</span><span class="r">{e.reason}</span></div>
          </li>
        {/each}
      </ol>
    </div>
    <div>
      <h2>Hypoglycaemia <span class="t-foot">{ds.hypoglycemiaEvents.length} documented episodes</span></h2>
      <ol class="evs">
        {#each ds.hypoglycemiaEvents as h (h.date)}
          <li class="hypo">
            <span class="d">{fmtDate(h.date)}</span>
            <div><span class="t"><b>{h.glucose} mg/dL</b> · {h.severity} · {h.medication}</span><span class="r">{h.context}</span></div>
          </li>
        {/each}
      </ol>
    </div>
  </section>
</div>

<style>
  .meds { display: flex; flex-direction: column; gap: var(--space-5); }
  h2 { margin-bottom: var(--space-3); display: flex; align-items: baseline; gap: var(--space-3); }
  .card { background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-4); }
  .curr { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: var(--space-3); }
  .mcard { display: flex; flex-direction: column; gap: 2px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-3) var(--space-4); }
  .mcard span:not(.t-foot):not(.for):not(.chip) { color: var(--text-2); }
  .for { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 4px; }
  .chip { font-size: var(--fs-foot); font-weight: 700; color: var(--accent); background: var(--accent-soft); border-radius: var(--radius-pill); padding: 0 7px; }
  .two { display: grid; grid-template-columns: 3fr 2fr; gap: var(--space-5); }
  @media (max-width: 900px) { .two { grid-template-columns: 1fr; } }
  .evs { list-style: none; border: 1px solid var(--line); border-radius: var(--radius-md); background: var(--surface); overflow: hidden; }
  .evs li { display: grid; grid-template-columns: 110px 1fr; gap: var(--space-3); padding: var(--space-2) var(--space-4); border-bottom: 1px solid var(--line); border-left: 3px solid var(--line-strong); }
  .evs li:last-child { border-bottom: none; }
  .evs li.start { border-left-color: var(--accent); } .evs li.increase { border-left-color: var(--warn); } .evs li.decrease { border-left-color: var(--ok); } .evs li.stop { border-left-color: var(--text-3); } .evs li.hypo { border-left-color: var(--danger); }
  .d { color: var(--text-3); font-size: var(--fs-foot); padding-top: 2px; }
  .evs div { display: flex; flex-direction: column; }
  .r { color: var(--text-2); font-size: var(--fs-foot); line-height: 1.5; }
</style>
